import { mutationOptions, queryOptions } from "@tanstack/react-query";
import {
  CHECK_BADGE_GRANT_STATUS,
  GET_BADGE_PROGRESS,
  GET_REVIEW_POSTING_ENABLED,
  GET_USER_REVIEW_LIST,
  GRANT_USER_BADGE,
  POST_USER_REVIEW,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { useAuthStore } from "~/stores/useAuthStore";
import type {
  BadgeGrantStatusResponse,
  BadgeProgressResponse,
  GrantBadgeRequest,
  PostReviewRequest,
  PostReviewResponse,
  ReviewListResponse,
  ReviewPostingEnabledResponse,
} from "./types";

export const reviewListQueryOptions = (targetUserId: string) =>
  queryOptions({
    queryKey: queryKeys.review.list(targetUserId),
    queryFn: () =>
      apiClient<ReviewListResponse>(GET_USER_REVIEW_LIST, {
        target_user_id: targetUserId,
      }),
  });

export const reviewPostingEnabledQueryOptions = (targetUserId: string) =>
  queryOptions({
    queryKey: queryKeys.review.postingEnabled(targetUserId),
    queryFn: () =>
      apiClient<ReviewPostingEnabledResponse>(GET_REVIEW_POSTING_ENABLED, {
        target_user_id: targetUserId,
      }),
    staleTime: 0,
  });

export const postReviewMutationOptions = mutationOptions({
  mutationFn: (data: PostReviewRequest) =>
    apiClient<PostReviewResponse>(POST_USER_REVIEW, {
      user_id: useAuthStore.getState().userId,
      target_user_id: data.targetUserId,
      score: data.score,
      ...(data.description ? { description: data.description } : {}),
    }),
  onSuccess: (_data, variables) => {
    // 投稿成功後にレビュー一覧と投稿可否キャッシュを無効化して最新情報を取得
    queryClient.invalidateQueries({
      queryKey: queryKeys.review.list(variables.targetUserId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.review.postingEnabled(variables.targetUserId),
    });
  },
});

export const badgeGrantStatusQueryOptions = (targetUserId: string) =>
  queryOptions({
    queryKey: queryKeys.review.badgeGrantStatus(targetUserId),
    queryFn: () =>
      apiClient<BadgeGrantStatusResponse>(CHECK_BADGE_GRANT_STATUS, {
        user_id: useAuthStore.getState().userId,
        target_user_id: targetUserId,
      }),
    staleTime: 0,
  });

// 獲得バッジ一覧（プロフィールの「獲得したバッジ」表示用）。男性が女性を見るときは
// target_user_id 側のバッジ、user_id は閲覧者（BE が閲覧者の性別/アプリで表示制御する）。
export const badgeProgressQueryOptions = (targetUserId: string) =>
  queryOptions({
    queryKey: queryKeys.review.badgeProgress(targetUserId),
    queryFn: () =>
      apiClient<BadgeProgressResponse>(GET_BADGE_PROGRESS, {
        user_id: useAuthStore.getState().userId,
        target_user_id: targetUserId,
      }),
  });

export const grantBadgeMutationOptions = mutationOptions({
  // BE はバッジ ID 配列を受け取り、複数バッジをまとめて付与する。data は返さない。
  mutationFn: (data: GrantBadgeRequest) =>
    apiClient<void>(GRANT_USER_BADGE, {
      user_id: useAuthStore.getState().userId,
      target_user_id: data.targetUserId,
      badge_id: data.badgeIds,
    }),
  onSuccess: (_data, variables) => {
    // 付与後は付与可否を無効化（not_granted_yet が false になり再付与不可になる）
    queryClient.invalidateQueries({
      queryKey: queryKeys.review.badgeGrantStatus(variables.targetUserId),
    });
  },
});
